import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { PortalCommunicationMessageItem } from "./PortalCommunicationMessageItem";

import styles from "./PortalCommunicationMessagesList.module.scss";

interface PortalCommunicationMessagesListProps {
    sharedItemData: SharedItem;
    patient: Patient;
    hideHeader?: boolean;
    onPriorityChange: (sharedItemMessageId: number, selectedPriority: SharedItemPriority) => void;
    onSharedItemMessageStatusToggleAsync?: (sharedItemMessageId: number, status: PortalSyncStatus.Revoked | PortalSyncStatus.Pending) => Promise<void>;
    onSharedItemAttachmentStatusToggleAsync?: (status: PortalSyncStatus.Revoked | PortalSyncStatus.Pending) => Promise<void>;
    onCategoryChange(categoryId: string): void;
}

function getOrCreateAttachmentMessage(sharedItem: SharedItem): SharedItemMessage {
    const initialMessage = sharedItem.Messages.firstOrDefault(
        message => message.DateCreated === sharedItem.DateCreated
        && message.CreatedByUserId === sharedItem.CreatedByUserId
    );

    return initialMessage
        ?? new SharedItemMessage({
            ...sharedItem,
            SentByContactId: sharedItem.InitiatedByContactId,
            Priority: SharedItemPriority.Normal
        });
}

function PortalCommunicationMessagesList(props: Readonly<PortalCommunicationMessagesListProps>): JSX.Element {
    const { getText } = useTranslation();

    if (!props?.sharedItemData)
        return null;

    const attachmentMessage = (props.sharedItemData.IncludeLinkedItem || props.sharedItemData.HasPreviouslyIncludedItem)
        ? getOrCreateAttachmentMessage(props.sharedItemData)
        : null;

    const sharedItemMessages = props.sharedItemData.Messages
        .filter(message => message.SharedItemMessageId !== attachmentMessage?.SharedItemMessageId)
        .sortByProperty(message => message.DateCreated);

    const renderPortalCommunicationMessageItem = (): JSX.Element => (
        <div>
            {attachmentMessage && (
                <PortalCommunicationMessageItem
                    key={attachmentMessage.SharedItemMessageId}
                    sharedItemMessage={attachmentMessage}
                    categoryId={props.sharedItemData.SharedItemCategoryId}
                    isUnCollapsed={sharedItemMessages.length === 0}

                    onPriorityChange={attachmentMessage.SharedItemMessageId
                        ? priority => props.onPriorityChange(attachmentMessage.SharedItemMessageId, priority)
                        : null
                    }
                    onSharedItemMessageStatusToggleAsync={attachmentMessage.SharedItemMessageId
                        ? status => props.onSharedItemMessageStatusToggleAsync?.(attachmentMessage.SharedItemMessageId, status)
                        : null
                    }
                    onSharedItemAttachmentStatusToggleAsync={props.onSharedItemAttachmentStatusToggleAsync}

                    onCategoryChange={categoryId => props.onCategoryChange(categoryId)}
                    isEditable={sharedItemMessages.length === 0 && !!attachmentMessage.SharedItemMessageId}
                    files={props.sharedItemData.Files}
                    sharedItemStatus={props.sharedItemData.Status}
                    includeLinkedItem={props.sharedItemData.IncludeLinkedItem}
                    sharedItemLastStatusDate={props.sharedItemData.LastStatusDate}
                />
            )}
            {sharedItemMessages.map(
                (message, index) => (
                    <PortalCommunicationMessageItem
                        key={message.SharedItemMessageId}
                        sharedItemMessage={message}
                        categoryId={props.sharedItemData.SharedItemCategoryId}
                        isUnCollapsed={index === sharedItemMessages.length - 1}
                        onPriorityChange={priority => props.onPriorityChange(message.SharedItemMessageId, priority)}
                        onSharedItemMessageStatusToggleAsync={status => props.onSharedItemMessageStatusToggleAsync?.(message.SharedItemMessageId, status)}
                        onCategoryChange={category => props.onCategoryChange(category)}
                        isEditable={index === sharedItemMessages.length - 1 && !!message.SharedItemMessageId}
                    />
                )
            )}
        </div>
    );

    return (
        <div>
            {!props.hideHeader && (
                <div className={styles.header}>
                    <span className={styles.headerText}>{getText("MessageDetailsLabel")}</span>
                    <span data-cy="portal-communications-messages-container-subject" className={styles.threadSubject}>{props.sharedItemData.Subject}</span>
                </div>
            )}
            {renderPortalCommunicationMessageItem()}
        </div>
    );
}

export { PortalCommunicationMessagesList };
